if (typeof debounce === 'undefined') {
  var debounce = function(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };
}
/**
 *  @class
 *  @function ThemeHeader
 */
if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = document.querySelector('.mobile-toggle-wrapper');

      // The drawer must live outside the header: .header-section creates a
      // stacking context (sticky, z-index 50) that sits below the
      // click-capture (52), so a fixed drawer left inside it could never
      // paint above the dim. It is rendered inside the header section for
      // access to the section's settings/blocks, then teleported to
      // #wrapper next to the other side panels. Stale copies from previous
      // section renders (theme editor) are removed first.
      const wrapper = document.getElementById('wrapper');
      if (this.menu && wrapper) {
        document.querySelectorAll('.mobile-menu-drawer').forEach((el) => {
          if (el !== this.menu) el.remove();
        });
        wrapper.appendChild(this.menu);
      }
      this._scrollTicking = false;
      this._stickyOffset = 0;

      // The drawer opens through OverlayManager, which provides the
      // click-capture, scroll lock, focus trap and Escape handling.
      // The hamburger state is synced from the panel events so every
      // close path (capture click, Escape, close button) resets it.
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        e.preventDefault();
        if (OverlayManager.isOpen(this.menu)) {
          OverlayManager.close(this.menu);
        } else {
          OverlayManager.open(this.menu);
        }
        window.dispatchEvent(new Event('resize.resize-select'));
      });
      document.addEventListener('panel:open', (e) => {
        if (e.detail.panel === this.menu) {
          this.toggle.classList.add('active');
        }
      });
      document.addEventListener('panel:closed', (e) => {
        if (e.detail.panel === this.menu) {
          this.toggle.classList.remove('active');
          this.toggle.removeAttribute('open');
          // The drawer's close paths reset nothing inside the menu, so whichever submenu the
          // visitor was in was still showing the next time they opened it, and which level
          // they landed on depended on how they had closed it last (NAV-002).
          //
          // Reset outright rather than through closeSubmenu: that one holds `open` for 400ms
          // so the panel can animate shut, which is right on screen and pointless here, where
          // the drawer has already gone. Off-screen, the level should simply be the first one.
          this.menu.querySelectorAll('details.link-container[open]').forEach((row) => {
            row.classList.remove('menu-opening');
            row.removeAttribute('open');
          });
          this.menu.querySelectorAll('.submenu-open').forEach((parent) => {
            parent.classList.remove('submenu-open');
          });
          this.menu.scrollTop = 0;
        }
      });

      // Cache sticky offset and recalculate on resize
      this._cacheStickyOffset();
      window.addEventListener('resize', debounce(() => {
        this._cacheStickyOffset();
      }, 100));

      // Single scroll listener with rAF throttle
      window.addEventListener('scroll', () => {
        if (!this._scrollTicking) {
          this._scrollTicking = true;
          requestAnimationFrame(() => {
            this.setStickyClass();
            this.setHeaderOffset();
            this.setHeaderHeight();
            this._scrollTicking = false;
          });
        }
      }, { passive: true });

      window.dispatchEvent(new Event('scroll'));

      if (document.querySelector('.announcement-bar-section')) {
        window.addEventListener('scroll', () => this.setAnnouncementHeight(), {
          passive: true
        });
        window.dispatchEvent(new Event('resize'));
      }

      // Buttons.
      this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      this.menu.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    }

    _cacheStickyOffset() {
      if (this.classList.contains('header-sticky--active')) {
        // Skip recalculation while sticky — the header is position:sticky at top:0,
        // so getBoundingClientRect().top would return 0 and corrupt the offset.
        if (this.classList.contains('is-sticky')) {
          return;
        }
        this._stickyOffset = parseInt(this.header_section.getBoundingClientRect().top, 10) + document.documentElement.scrollTop;
      }
    }
    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        this.classList.toggle('is-sticky', window.scrollY >= this._stickyOffset && window.scrollY > 0);
      }
    }
    setAnnouncementHeight() {
      const a_bar = document.getElementById('shopify-section-announcement-bar');
      if (a_bar) {
        let h = a_bar.clientHeight;
        document.documentElement.style.setProperty('--announcement-height', h + 'px');
      }
    }
    setHeaderOffset() {
      let h = this.header_section.getBoundingClientRect().top;
      document.documentElement.style.setProperty('--header-offset', h + 'px');
    }
    setHeaderHeight() {
      let h = this.clientHeight;
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentLinksEnabled = this.menu.dataset.parentLinks === 'true';

      // When parent links setting is on, only arrow opens submenu
      if (parentLinksEnabled) {
        event.preventDefault();
        const isArrowClick = event.target.closest('.link-forward');

        if (!isArrowClick) {
          const anchor = summaryElement.querySelector('a');
          if (anchor && anchor.href) {
            window.location.href = anchor.href;
          }
          return;
        }
        // Arrow clicked — manually open details
        detailsElement.setAttribute('open', '');
      }

      const parentMenuElement = detailsElement.closest('.link-container');

      // Stamp deferred template content on first open
      const tpl = detailsElement.querySelector(':scope > template.mobile-menu-deferred');
      if (tpl) {
        tpl.replaceWith(tpl.content.cloneNode(true));
        // Re-bind back buttons for the newly stamped content
        detailsElement.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
        detailsElement.querySelectorAll('details.link-container summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      }

      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }

      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}
/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    connectedCallback() {
      this.bindParentItems();
      this.measureMegaMenus();

      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');

      if (!this.submenus.length) {
        return;
      }
      // resize on initial load
      window.addEventListener('resize', debounce(() => {
        this.resizeSubMenus();
      }, 100));

      window.dispatchEvent(new Event('resize'));

      document.fonts.ready.then(() => {
        this.resizeSubMenus();
      });
    }
    /**
     * The dropdowns open on :hover and :focus-within and nothing else, which is a pointer
     * assumption the store's own tablets do not meet (NAV-003). SHOP appeared to work only by
     * accident: its href is "#", so tapping it focused without going anywhere and the panel
     * opened. GROUP BUYS has a real page, so the first tap left for it and its four links were
     * never shown to a finger at all. Two items side by side, behaving differently, for a
     * reason nothing on screen explains.
     *
     * This works WITH the CSS rather than against it. Focus is what opens the panel, so a tap
     * on touch keeps the focus and cancels only the navigation; a second tap blurs, which is
     * what closes it. Nothing here duplicates the reveal, so there is no second source of truth
     * about whether a menu is open.
     */
    bindParentItems() {
      const coarse = window.matchMedia('(hover: none)');

      this.querySelectorAll('.menu-item-has-children > .thb-full-menu--link').forEach((link) => {
        link.addEventListener('click', (event) => {
          // "#" is not a destination. Following it adds a /# entry to history on every click,
          // so the back button walks through phantom pages (NAV-004). Cancelled everywhere,
          // not only on touch, because it is never useful.
          const placeholder = link.getAttribute('href') === '#';

          if (!coarse.matches) {
            if (placeholder) event.preventDefault();
            return;
          }

          event.preventDefault();
          const open = link.getAttribute('aria-expanded') === 'true';
          this.closeAllParents();
          if (!open) {
            link.setAttribute('aria-expanded', 'true');
            link.focus();
          } else {
            link.blur();
          }
        });

        // Focus and the attribute have to agree, or a panel opened by Tab would report itself
        // closed and the next tap would try to open what is already open.
        link.addEventListener('focus', () => link.setAttribute('aria-expanded', 'true'));
        link.addEventListener('blur', () => link.setAttribute('aria-expanded', 'false'));
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const item = event.target.closest('.menu-item-has-children');
        if (!item) return;
        // Escape has to beat :focus-within, and returning focus to the opener (which is what
        // the visitor expects) would otherwise hold the panel open. The class outranks the
        // reveal for exactly as long as focus stays inside, and lifts the moment it leaves.
        item.classList.add('ktechs-menu-dismissed');
        const opener = item.querySelector(':scope > .thb-full-menu--link');
        if (opener) opener.focus();
      });

      this.addEventListener('focusout', (event) => {
        const item = event.target.closest('.menu-item-has-children');
        if (!item) return;
        requestAnimationFrame(() => {
          if (!item.contains(document.activeElement)) {
            item.classList.remove('ktechs-menu-dismissed');
          }
        });
      });

      document.addEventListener('click', (event) => {
        if (!coarse.matches || this.contains(event.target)) return;
        this.closeAllParents();
      });
    }

    /**
     * A translucent header and its open mega menu are one sheet of glass (merchant, 16 Sep). Two
     * surfaces each blur only what lies under their own box, so a header and a panel blurred apart
     * met at a visible seam. The glass is drawn from the menu item instead, from the top of the
     * header to the bottom of its panel, and CSS cannot know how tall the panel is: this hands the
     * height over, and the class holds the panel's own glass back until it has.
     */
    measureMegaMenus() {
      const items = this.querySelectorAll('.thb-full-menu > li.menu-item-has-megamenu');
      if (!items.length || typeof ResizeObserver === 'undefined') return;

      const observer = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const item = entry.target.parentElement;
          item.style.setProperty('--ktechs-mega-height', `${entry.target.offsetHeight}px`);
          item.classList.add('ktechs-mega-measured');
        });
      });

      items.forEach((item) => {
        const panel = item.querySelector(':scope > .sub-menu');
        if (panel) observer.observe(panel);
      });
    }

    closeAllParents() {
      this.querySelectorAll('.thb-full-menu--link[aria-expanded="true"]').forEach((other) => {
        other.setAttribute('aria-expanded', 'false');
        other.blur();
      });
    }

    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.parentElement.clientWidth + 10,
            total = w + l;
          if (total > document.body.clientWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}
